import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { invalidateQueries, useMutation } from '@globalconnect/query';
import { untypedIsEnabled } from '@ip-only/client-features';
import { ObjectWithProps } from '@ip-only/front-end-library';
import { combineLatest, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { InstallationReportingV2Service } from '../../../installation-reporting-app/service/sdu/installation-reporting-v2.service';
import { DetailedErrorFeedbackAlertService } from '../../../shared/feedback-alert/service/detailed-error-feedback-alert.service';
import { FeedbackAlertService } from '../../../shared/feedback-alert/service/feedback-alert.service';
import { CompleteReportConfirmationState } from '../../../shared/installation-report-content-v2/complete-report-confirmation-modal/complete-report-confirmation-modal.component';
import {
  CombinedDataV2,
  InstallationReportCompleteStatus,
} from '../../../shared/installation-report-content-v2/installation-report-content-entities';
import {
  deliveryItemsKey,
  installationReportsForProjectKey,
} from '../../../shared/installation-report-content-v2/service/abstract-installation-report.service';
import { InstallationReportReadOnlyService } from '../../../shared/installation-report-content-v2/service/installation-report-read-only.service';
import { UpdateReportService } from '../../../shared/installation-report-content-v2/service/update-report.service';
import {
  onErrorCompleteInstallationReport,
  onErrorUpdateInstallationReport,
  onSuccessUpdateInstallationReport,
} from '../../../shared/installation-report-content-v2/utils/handle-gcquery-request';
import { updateInstallationReportRequestBody } from '../../../shared/installation-report-content-v2/utils/handle-request-objects';
import {
  getCompleteReportPrimaryButtonLabelKey,
  getCompleteReportSecondaryButtonTooltipKey,
  getInstallationReportCompleteButtonState,
  InstallationReportCompleteButtonState,
  isCompleteReportPrimaryButtonEnabled,
} from '../../../shared/installation-report-content-v2/utils/is-installation-report-fully-complete';
import {
  RP_INSTALLATION_REPORT_CLOSED,
  RP_INSTALLATION_REPORT_COMPLETED,
  RP_INSTALLATION_REPORT_SAVE_ATTEMPT,
  RP_INSTALLATION_REPORT_UPDATED,
} from '../../../shared/posthug-analytics/analytics-event-types';
import { AnalyticsService } from '../../../shared/posthug-analytics/analytics.service';
import { isTopLevelDomainFI } from '../../../shared/util/deploy-helper';
import { CONSTRUCTION_RESPONSIBLE } from '../../../user-roles-def/user-roles';
import { UserService } from '../../../user/user.service';
import {
  MultiPointModalData,
  ReportModalService,
} from '../report-modal.service';

@Component({
  selector: 'app-installation-report-modal-v2',
  templateUrl: './installation-report-modal-v2.component.html',
  styleUrls: ['./installation-report-modal-v2.component.scss'],
  standalone: false,
})
export class InstallationReportModalV2Component implements OnInit, OnDestroy {
  @Input() ifs: string;
  @Input() deliveryOrderId: string | undefined;
  @Output() dismiss = new EventEmitter();

  @Input({ required: false }) multiPointData: MultiPointModalData | undefined;

  combinedData$: Observable<CombinedDataV2>;
  completeReportButtonState$: Observable<InstallationReportCompleteButtonState | null>;

  form: UntypedFormGroup = this.formBuilder.group({});

  arcEnabled = untypedIsEnabled('arc');
  isDeliveryPortalFi = isTopLevelDomainFI();

  readonly isCompleteReportPrimaryButtonEnabled =
    isCompleteReportPrimaryButtonEnabled;

  confirmationState: 'closed' | CompleteReportConfirmationState = 'closed';

  private completeMutationIsUnlock = false;

  installationReportUpdateMutation = useMutation(
    this.installationReportingV2service.updateInstallationReportV2$,
    {
      onSuccess: () => {
        if (this.deliveryOrderId) {
          onSuccessUpdateInstallationReport(
            this.ifs,
            this.deliveryOrderId,
            this.feedbackAlertService,
          );
        }
        this.saveMacAddress();
        this.updateReportService.setReportSaved();
        this.analyticsService.logEvent(RP_INSTALLATION_REPORT_UPDATED);
      },
      onError: () => onErrorUpdateInstallationReport(this.feedbackAlertService),
    },
  );

  installationReportCompleteMutation = useMutation(
    this.installationReportingV2service.completeInstallationReportV2$,
    {
      onSuccess: () => {
        if (this.deliveryOrderId) {
          onSuccessUpdateInstallationReport(
            this.ifs,
            this.deliveryOrderId,
            this.feedbackAlertService,
          );
        }
        this.analyticsService.logEvent(RP_INSTALLATION_REPORT_COMPLETED);
        this.confirmationState = this.completeMutationIsUnlock
          ? 'closed'
          : 'completed';
      },
      onError: (error) => {
        onErrorCompleteInstallationReport(
          error,
          this.detailedErrorFeedbackAlertService,
          this.feedbackAlertService,
        );
        this.confirmationState = this.completeMutationIsUnlock
          ? 'closed'
          : 'accept';
      },
    },
  );

  private cancelSubs$ = new Subject<void>();

  constructor(
    private installationReportingV2service: InstallationReportingV2Service,
    private formBuilder: UntypedFormBuilder,
    private feedbackAlertService: FeedbackAlertService,
    private readonly detailedErrorFeedbackAlertService: DetailedErrorFeedbackAlertService,
    private analyticsService: AnalyticsService,
    private updateReportService: UpdateReportService,
    private modalService: ReportModalService,
    private userService: UserService,
    private readonly readOnlyService: InstallationReportReadOnlyService,
  ) {}

  ngOnInit(): void {
    document.body.classList.add('no-scroll');
    document.documentElement.classList.add('no-scroll');
    if (this.deliveryOrderId) {
      this.installationReportingV2service.setActiveReportContext(
        this.ifs,
        this.deliveryOrderId,
      );
    }
    this.getData();
  }

  ngOnDestroy(): void {
    this.installationReportingV2service.clearActiveReportContext();
    this.cancelSubs$.next();
    this.cancelSubs$.complete();
    document.body.classList.remove('no-scroll');
    document.documentElement.classList.remove('no-scroll');
  }

  getData() {
    if (this.deliveryOrderId) {
      this.combinedData$ = combineLatest([
        this.installationReportingV2service.getInstallationReportByIfsAndOpportunityDeliveryOrderIdV2(
          this.ifs,
          this.deliveryOrderId,
        ),
        this.installationReportingV2service.getDeliveryItem$(
          this.ifs,
          this.deliveryOrderId,
        ),
      ]).pipe(
        takeUntil(this.cancelSubs$),
        map(([installationReport, deliveryItem]) => ({
          installationReport,
          deliveryItem,
        })),
      );

      this.completeReportButtonState$ = this.combinedData$.pipe(
        takeUntil(this.cancelSubs$),
        map(({ installationReport }) =>
          getInstallationReportCompleteButtonState(
            this.hasPermittedRoles(),
            installationReport.data,
          ),
        ),
      );
    }
  }

  onProceed() {
    this.readOnlyService.whenEditable(() => {
      if (!this.form.valid || !this.deliveryOrderId) {
        this.feedbackAlertService.showAlert(
          'critical',
          'installationReportV2.modal.onProceedError',
        );
        return;
      }
      this.installationReportUpdateMutation.mutate(
        updateInstallationReportRequestBody(this.form, this.deliveryOrderId),
        this.form.get('id')?.value,
      );
    });
    this.analyticsService.logEvent(RP_INSTALLATION_REPORT_SAVE_ATTEMPT);
  }

  getCompleteButtonLabelKeyForState(
    state: InstallationReportCompleteButtonState | null | undefined,
  ): string | null {
    return getCompleteReportPrimaryButtonLabelKey(
      this.isDeliveryPortalFi,
      this.arcEnabled,
      state ?? null,
    );
  }

  getCompleteButtonTooltipKeyForState(
    state: InstallationReportCompleteButtonState | null | undefined,
  ): string | null {
    return getCompleteReportSecondaryButtonTooltipKey(
      this.isDeliveryPortalFi,
      this.arcEnabled,
      state ?? null,
    );
  }

  isCombinedDataLoading(combinedData: CombinedDataV2): boolean {
    return (
      combinedData.installationReport.isLoading ||
      combinedData.deliveryItem.isLoading
    );
  }

  onCompleteReportButtonClick(
    state: InstallationReportCompleteButtonState | null | undefined,
  ) {
    if (state === 'completeEnabled') {
      this.confirmationState = 'accept';
      return;
    }
    this.performCompleteOrUnlock();
  }

  onConfirmComplete() {
    this.performCompleteOrUnlock();
  }

  onDismissConfirmation() {
    this.confirmationState = 'closed';
  }

  performCompleteOrUnlock() {
    if (!this.deliveryOrderId) {
      this.feedbackAlertService.showAlert(
        'critical',
        'installationReportV2.modal.onProceedError',
      );
      this.analyticsService.logEvent(RP_INSTALLATION_REPORT_COMPLETED);
      return;
    }

    this.combinedData$
      .pipe(
        take(1),
        map(({ installationReport }) => ({
          status: installationReport.data?.isCompleted
            ? ({ pathValue: 'unlock' } as InstallationReportCompleteStatus)
            : ({ pathValue: 'complete' } as InstallationReportCompleteStatus),
          isUnlock: !!installationReport.data?.isCompleted,
          reportId:
            this.form.get('id')?.value ?? installationReport.data?.id ?? null,
        })),
      )
      .subscribe(({ status, isUnlock, reportId }) => {
        if (!reportId) {
          this.feedbackAlertService.showAlert(
            'critical',
            'installationReportV2.modal.onProceedError',
          );
          return;
        }
        if (!isUnlock && !this.form.valid) {
          this.feedbackAlertService.showAlert(
            'critical',
            'installationReportV2.modal.onProceedError',
          );
          return;
        }
        this.completeMutationIsUnlock = isUnlock;
        this.installationReportCompleteMutation.mutate(status, reportId);
      });
    this.analyticsService.logEvent(RP_INSTALLATION_REPORT_COMPLETED);
  }

  saveMacAddress() {
    if (
      ((this.form.value as ObjectWithProps).PRE_INSTALLATION as ObjectWithProps)
        ?.macAddress
    ) {
      this.updateReportService.setSavedMacAddress(
        (
          (this.form.value as ObjectWithProps)
            .PRE_INSTALLATION as ObjectWithProps
        )?.macAddress,
      );
    }
  }

  hasPermittedRoles() {
    return this.userService.checkAccess([CONSTRUCTION_RESPONSIBLE]);
  }

  closeModal() {
    this.dismiss.emit();
    this.analyticsService.logEvent(RP_INSTALLATION_REPORT_CLOSED);
    this.openMultiPointModal();
  }

  openMultiPointModal() {
    if (this.multiPointData?.data.hc) {
      this.modalService.setModalState({
        multiPointModal: {
          open: true,
          multiPoint: this.multiPointData,
        },
      });
    }
    invalidateQueries([deliveryItemsKey, this.ifs]);
    invalidateQueries([installationReportsForProjectKey, this.ifs]);
  }
}
